import { Button, Group, Modal, TextInput } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { updateCategory } from "api/category.service";
import { safeApiCall } from "api/common.service";
import {
  MantineNotificationColor,
  MantineNotificationPosition,
} from "common/constants";
import { useEffect, useState } from "react";
import { Brands, Categories, Category } from "types/model.types";

interface ICategoryModal {
  opened: boolean;
  onClose: () => void;
  category: Category | null;
  existingCategories: Brands;
  setCategories: React.Dispatch<React.SetStateAction<Categories>>;
  forceReload?: () => void;
}

const CategoryModal: React.FC<ICategoryModal> = ({
  opened,
  onClose,
  category,
  existingCategories,
  setCategories,
  forceReload,
}) => {
  const [name, setName] = useState("");

  useEffect(() => {
    setName(category?.name ?? "");
  }, [category]);

  // Handle form submission for adding or editing a brand
  // This function checks for duplicate brand names and either adds or updates the brand
  const handleSubmit = async () => {
    const trimmedName = name.trim();
    const isDuplicate = existingCategories.some(
      (c) =>
        c.name.trim().toLowerCase() === trimmedName.toLowerCase() &&
        c.id !== category?.id
    );

    if (isDuplicate) {
      notifications.show({
        title: "Category Management",
        message: "Category with this name already exists.",
        color: MantineNotificationColor.Warn,
        position: MantineNotificationPosition.TopCenter,
      });
      return;
    }

    const payload: Category = {
      id: category?.id ?? 0,
      name: trimmedName,
      total: 0, // Assuming total is not needed for add/edit
    };

    try {
      await safeApiCall(updateCategory(payload));

      notifications.show({
        title: "Category Management!",
        message: `Category "${payload.name}" saved successfully.`,
        color: MantineNotificationColor.Success,
        position: MantineNotificationPosition.TopCenter,
      });
      forceReload?.();
      onClose();
    } catch (error) {
      console.error("Error saving brand:", error);
      notifications.show({
        title: "Category Management!",
        message: "Something went wrong. Please try again.",
        color: MantineNotificationColor.Error,
        position: MantineNotificationPosition.TopCenter,
      });
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={category?.id ? "Edit Category" : "Add Category"}
      centered
    >
      <TextInput
        label="Category Name"
        value={name}
        onChange={(e) => setName(e.currentTarget.value)}
        placeholder="Enter category name"
        mb="md"
        autoFocus
      />
      <Group justify="flex-end">
        <Button onClick={handleSubmit}>
          {category?.id ? "Update" : "Add"}
        </Button>
      </Group>
    </Modal>
  );
};

export default CategoryModal;
